import { TGameListItem } from '@/apis/hub88';
import LayoutWebCoreApp from '@/components/system/Layout/LayoutWebCore';
import InitializeTelegramMiniApp from '@/components/telegram/InitializeTelegramMiniApp';
import { ConfirmConfig } from '@/components/ui/ConfirmModal/config';
import { THEME_MODE_ENU } from '@/constants/global';
import GlobalConfigContextWrapper from '@/contexts';
import busEvents from '@/helper/busEvents';
import {
  renderMetaCanonicalTags,
  renderMetaDescription,
  renderMetaTitle,
} from '@/helper/seo';
import AmplitudeHeader from '@/helper/seo/AmplitudeHeader';
import setZendeskSettings from '@/helper/setZendeskSettings';
import TmaSDKLoader from '@/helper/telegram/TmaSDKLoader';
import useAppMounted from '@/hooks/biz/useAppMounted';
import useUserLoginLogout from '@/hooks/biz/useUserLoginLogout';
import useNProgress from '@/hooks/ui/useNProgress';
import useVConsole from '@/hooks/useVConsole';
import { betbyBtRenderURL } from '@/pages/sports';
import { appGameList, appSidebarOpenState } from '@/state/globalSettings';
import { deviceInfoAtom } from '@/state/globalSettings/deviceInfo';
import { store } from '@/state/store';
import {
  isFomoBoxGame,
  isWoWSpaceView,
  isProduction,
  isSquidGame,
  isTelegramMiniAppView,
} from '@/utils/checkRuntime';
import { DeviceInfo } from '@/utils/deviceType';
import gtm from '@/utils/googleTagManager';
import { getStaticAsset } from '@/utils/staticAsset';
import CssBaseline from '@mui/material/CssBaseline';
import * as Sentry from '@sentry/nextjs';
import { useMount } from 'ahooks';
import { Provider } from 'jotai';
import { useHydrateAtoms } from 'jotai/utils';
import { ConfirmProvider } from 'material-ui-confirm';
import { appWithTranslation, UserConfig, useTranslation } from 'next-i18next';
import { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { ReactElement, ReactNode } from 'react';
import 'nprogress/nprogress.css';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import i18nConfig from '../next-i18next.config';
import './styles.css';
import '../styles/globals.css';
import { useZendeskStatus } from '@/hooks/biz/useZendeskStatus';
import { useCheckInvitationCode } from '@/hooks/invitation';
import useUserLoginCallbackHandler from '@/hooks/biz/useUserLoginCallbackHandler';
import thinkingdata from 'thinkingdata-browser';
import { useEffect, useState } from 'react';
import TrackingProvider from '@/contexts/TrackProvider';
import GoogleAnalytics from '@/components/GoogleAnalytics';
import { globalQueryKeyReferralCode } from '@/constants/globalQueryKeyMaps';
import { saveReferralCode } from '@/utils/referralCode';
import { saveRedirectPath, shouldSavePath } from '@/utils/redirectPath';

// PWA components
const PWAUpdatePrompt = dynamic(
  () => import('@/components/common/PWAUpdatePrompt'),
  {
    ssr: false,
  },
);

const InstallPWA = dynamic(() => import('@/components/common/InstallPWA'), {
  ssr: false,
});

const ControlInviteTgModal = dynamic(
  () => import('@/components/system/InviteTgModal'),
  { ssr: false },
);

const MediaQueryVisual = dynamic(
  () => import('@/components/ui/Devtools/MediaQueryVisual'),
  { ssr: false },
);

// Custom PWA Install Prompt Component
const PWAInstallPrompt = dynamic(
  () => import('@/components/ui/PWAInstallPrompt'),
  { ssr: false },
);

const TAconfig = {
  appId: '7d6d275740104c76ae9b65912baca30f',
  name: 'Ta',
  serverUrl: 'https://te.pinamania.com',
  autoTrack: {
    pageShow: true,
    pageHide: true,
  },
};
// import {
//   APP_SIDE_BAR_OPEN_STATUS,
//   THEME_MODE,
// } from '@/constants/cookieStorageKeys';

import { NextPageWithLayout } from '@/types/common';

export interface MyAppProps extends AppProps {
  Component: NextPageWithLayout;
  themeMode?: THEME_MODE_ENU;
  appSidebarOpened?: boolean;
  deviceInfo?: DeviceInfo;
  gameList?: TGameListItem[];
  // userInfo?: IUserInfo;
}

const SsrHydrator = ({ hydrateData, children }) => {
  useHydrateAtoms(hydrateData, {
    store: store,
  });
  return <>{children}</>;
};
// export const runtime = 'experimental-edge';

const AppMounted = () => {
  useAppMounted();
  return <></>;
};

const CrownBitApp = (
  props: // userInfo,
  MyAppProps,
) => {
  const {
    Component,
    pageProps,
    themeMode = THEME_MODE_ENU.Dark,
    appSidebarOpened = true,
    deviceInfo = {
      isMobile: false,
      isDesktop: true,
      isTablet: false,
    },
  } = props;

  const { asPath, locale, push, query } = useRouter();
  const helmetContext = {};
  const { t } = useTranslation();
  const { logout } = useUserLoginLogout();
  const { pathname } = useRouter();

  // PWA installation state
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);

  // Check if app is already installed
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      (window.matchMedia('(display-mode: standalone)').matches ||
        (window.navigator as any).standalone === true)
    ) {
      setIsAppInstalled(true);
    }

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      console.log('beforeinstallprompt event was fired');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      setIsAppInstalled(true);
      setDeferredPrompt(null);
      console.log('PWA was installed');
    });

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt,
      );
    };
  }, []);

  // Save referral code to local storage when URL contains it
  useEffect(() => {
    const refCode = query[globalQueryKeyReferralCode] as string;
    if (refCode) {
      saveReferralCode(refCode);
    }
  }, [query]);

  // Save current path for redirection after login/register
  useEffect(() => {
    if (shouldSavePath(asPath)) {
      saveRedirectPath(asPath);
    }
  }, [asPath]);

  const isMobileCasinoPage = pathname?.includes('/casino/mobile-play');
  const notLayout =
    pathname?.includes('/thirdLoginCallback') || isMobileCasinoPage;

  const { toggleZendesk, closeZendesk } = useZendeskStatus();

  useMount(() => {
    closeZendesk();
    busEvents.on('toggleZendeskStatus', toggleZendesk);
  });

  useCheckInvitationCode();
  useNProgress();
  useVConsole(deviceInfo?.isMobile);

  useMount(() => {
    busEvents.on('userLogout', () => {
      logout();
      push('/');
    });

    if (isTelegramMiniAppView) {
      logout();
    }
  });

  // 支持页面级别的 getLayout 函数
  const getLayout =
    Component.getLayout ??
    ((page) => (
      <LayoutWebCoreApp>
        <TrackingProvider />
        {page}
      </LayoutWebCoreApp>
    ));

  const AppBox = notLayout ? (
    <Component {...pageProps} />
  ) : (
    getLayout(<Component {...pageProps} />)
  );

  const { gameList = [] } = pageProps;

  const hydrateData = [
    [deviceInfoAtom, deviceInfo],
    [appSidebarOpenState, Boolean(appSidebarOpened)],
    gameList.length
      ? [appGameList, { loading: false, list: gameList ?? [] }]
      : undefined,
  ].filter(Boolean);

  const AppCoreComponent = () => {
    return (
      <Sentry.ErrorBoundary
        fallback={({ error, componentStack, resetError }) => (
          <div>
            <h1>{t('An error occurred')}</h1>
            <pre>{error?.message}</pre>
            <pre>{componentStack}</pre>
            <button onClick={resetError}>Reset</button>
          </div>
        )}
        onError={(err) => Sentry.captureException(err)}
      >
        <GlobalConfigContextWrapper themeMode={themeMode}>
          <ConfirmProvider defaultOptions={ConfirmConfig}>
            <CssBaseline enableColorScheme />
            {!isProduction && <MediaQueryVisual />}
            <AppMounted />
            {/* {!isAppInstalled && deferredPrompt && <PWAInstallPrompt deferredPrompt={deferredPrompt} />} */}
            {isTelegramMiniAppView ? (
              <InitializeTelegramMiniApp>{AppBox}</InitializeTelegramMiniApp>
            ) : (
              AppBox
            )}
          </ConfirmProvider>
        </GlobalConfigContextWrapper>
      </Sentry.ErrorBoundary>
    );
  };

  const getMoonClubHelmet = () => {
    return (
      <Helmet>
        <meta
          key="meta:viewport"
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, shrink-to-fit=no, viewport-fit=cover"
        />
        {renderMetaTitle(t('The Highest Rewarding Crypto Online Casino'))}
        {renderMetaDescription(
          t(
            'MoonClub Casino rewards money back on every bet, offers up to 25% referral commission, and is available to play via Telegram. Get started with a 125% bonus!',
          ),
        )}
        {renderMetaCanonicalTags({
          pathname: asPath,
          lang: locale,
        })}
        <meta
          key="meta:og:image"
          property="og:image"
          content={getStaticAsset('/assets/images/logo/Rocket.svg')}
        />
        <meta
          key="meta:og:image:type"
          property="og:image:type"
          content="image/svg"
        />
        <meta
          key="meta:og:image:width"
          property="og:image:width"
          content="600"
        />
        <meta
          key="meta:og:image:height"
          property="og:image:height"
          content="315"
        />
        <meta property="og:title" content="Moonclub" />
        <meta property="og:site_name" content="Moonclub" />
        <meta property="og:type" content="website" />
        <meta
          key="meta:twitter:card"
          name="twitter:card"
          content="summary_large_image"
        />
        <link
          rel="icon"
          type="image/svg"
          sizes="128x128"
          href={getStaticAsset('/assets/images/logo/Rocket.svg')}
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin={'anonymous'}
        />
      </Helmet>
    );
  };

  const getWoWSpaceHelmet = () => {
    return (
      <Helmet>
        <meta
          key="meta:viewport"
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, shrink-to-fit=no, viewport-fit=cover"
        />
        {renderMetaTitle(t('WoW Space - the fun degen pump pvp/pve space'))}
        {renderMetaDescription(
          t(
            'WoW Space is your gateway to space exploration through gaming. Enjoy a variety of games and collect rewards as you journey through the cosmos.',
          ),
        )}
        {renderMetaCanonicalTags({
          pathname: asPath,
          lang: locale,
        })}
        <meta
          key="meta:og:image"
          property="og:image"
          content={getStaticAsset('/assets/images/logo/Wow_Space_logo.png')}
        />
        <meta
          key="meta:og:image:type"
          property="og:image:type"
          content="image/png"
        />
        <meta
          key="meta:og:image:width"
          property="og:image:width"
          content="600"
        />
        <meta
          key="meta:og:image:height"
          property="og:image:height"
          content="315"
        />
        <meta property="og:title" content="WoW Space" />
        <meta property="og:site_name" content="WoW Space" />
        <meta property="og:type" content="website" />
        <meta
          key="meta:twitter:card"
          name="twitter:card"
          content="summary_large_image"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="128x128"
          href={getStaticAsset('/assets/images/logo/Wow_Space_logo_fav.png')}
        />
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          rel="preconnect"
          href="https://fonts.gstatic.com"
          crossOrigin={'anonymous'}
        />
      </Helmet>
    );
  };

  const getHelmet = () => {
    return isWoWSpaceView ? getWoWSpaceHelmet() : getMoonClubHelmet();
  };

  return (
    <>
      <Provider store={store}>
        <SsrHydrator hydrateData={hydrateData}>
          <HelmetProvider context={helmetContext}>
            {getHelmet()}
            {!isWoWSpaceView && <AmplitudeHeader />}
            <Script src={betbyBtRenderURL} strategy={'lazyOnload'} />
            {/*Start of moonclub Zendesk Widget script*/}
            <Script
              id="ze-snippet"
              src="https://static.zdassets.com/ekr/snippet.js?key=7429574d-8a72-46f1-ada2-264cad91148b"
              strategy="lazyOnload"
              onLoad={() => {
                setZendeskSettings();
              }}
            />
            <Script id="analytics-script" strategy="afterInteractive">
              {`
        !function(){var analytics=window.analytics=window.analytics||[];if(!analytics.initialize)if(analytics.invoked)window.console&&console.error&&console.error("Snippet included twice.");else{analytics.invoked=!0;analytics.methods=["trackSubmit","trackClick","trackLink","trackForm","pageview","identify","reset","group","track","ready","alias","debug","page","once","off","on","addSourceMiddleware","addIntegrationMiddleware","setAnonymousId","addDestinationMiddleware"];analytics.factory=function(e){return function(){var t=Array.prototype.slice.call(arguments);t.unshift(e);analytics.push(t);return analytics}};for(var e=0;e<analytics.methods.length;e++){var key=analytics.methods[e];analytics[key]=analytics.factory(key)}analytics.load=function(key,e){var t=document.createElement("script");t.type="text/javascript";t.async=!0;t.src="https://cdp-eu.customer.io/v1/analytics-js/snippet/" + key + "/analytics.min.js";var n=document.getElementsByTagName("script")[0];n.parentNode.insertBefore(t,n);analytics._writeKey=key;analytics._loadOptions=e};analytics.SNIPPET_VERSION="4.15.3"; analytics.load("d500e24253869b296fd6"); analytics.page(); }}();
      `}
            </Script>
            {/* End of moonclub Zendesk Widget script */}
            <link rel="preconnect" href="//www.googletagmanager.com" />
            {gtm.isEnabled && (
              <script dangerouslySetInnerHTML={{ __html: gtm.getInitCode() }} />
            )}
            {/* PWA Components */}
            {/* <PWAUpdatePrompt /> */}
            {/* {!isTelegramMiniAppView && <InstallPWA className="fixed bottom-4 right-4 z-50" />} */}
            {isTelegramMiniAppView ? (
              <TmaSDKLoader>{AppCoreComponent()}</TmaSDKLoader>
            ) : (
              AppCoreComponent()
            )}
          </HelmetProvider>
        </SsrHydrator>
      </Provider>
      <GoogleAnalytics />
    </>
  );
};

// @ts-ignore
export default appWithTranslation(CrownBitApp, i18nConfig as UserConfig);
